import { css, html, LitElement } from 'lit'
import { property, state } from 'lit/decorators.js'
import { safeCustomElement } from '@/utilities/custom-element'
import type { HeaderAccordionLink } from './types'

@safeCustomElement('mdc-header-accordion')
export class MdcHeaderAccordion extends LitElement {
  @property({ type: String }) title = ''
  @property({ type: Array }) links: HeaderAccordionLink[] = []
  @property({ type: Boolean }) open = false

  @state() private isOpen = false

  connectedCallback() {
    super.connectedCallback()
    this.isOpen = this.open
  }

  updated(changedProperties: Map<string | number | symbol, unknown>) {
    if (changedProperties.has('open')) {
      this.isOpen = this.open
    }
  }

  static styles = css`
		:host {
			display: block;
			font-family: var(--typography-font-family-sans);
		}

		.mdc-header-accordion {
			display: flex;
			flex-direction: column;
		}

		.mdc-header-accordion__title {
			display: flex;
			align-items: center;
			justify-content: space-between;
			padding: 0.75rem 1rem;
			background: none;
			border: none;
			cursor: pointer;
			font-size: 0.875rem;
			color: var(--color-functional-gray-black, #111111);
			font-weight: 450;
			line-height: 1.3;
			text-align: left;
			width: 100%;
		}

		.mdc-header-accordion__title:hover {
			background-color: var(--color-functional-gray-25, #fafafa);
		}

		.mdc-header-accordion__chevron {
			width: 0.625rem;
			height: 0.625rem;
			border-right: 1px solid currentColor;
			border-bottom: 1px solid currentColor;
			transform: rotate(45deg);
			transition: transform 0.3s ease;
		}

		.mdc-header-accordion--opened .mdc-header-accordion__chevron {
			transform: rotate(-135deg);
		}

		.mdc-header-accordion__links-container {
			max-height: 0;
			overflow: hidden;
			transition: max-height 0.3s ease;
		}

		.mdc-header-accordion--opened .mdc-header-accordion__links-container {
			max-height: 100vh;
		}

		.mdc-header-accordion__links {
			list-style: none;
			padding: 0;
			margin: 0;
		}

		.mdc-header-accordion__link-item {
			padding: 0.5rem 1rem;
		}

		.mdc-header-accordion__link {
			display: block;
			color: var(--color-functional-gray-black, #111111);
			text-decoration: none;
			font-size: 0.875rem;
			font-weight: 450;
			line-height: 1.3;
		}

		.mdc-header-accordion__link:hover {
			text-decoration: underline;
		}
	`

  private toggleOpen() {
    this.isOpen = !this.isOpen
    this.dispatchEvent(
      new CustomEvent('toggle', {
        detail: { open: this.isOpen },
        bubbles: true,
        composed: true,
      })
    )
  }

  render() {
    const className = `mdc-header-accordion${this.isOpen ? ' mdc-header-accordion--opened' : ''}`

    return html`
			<div class=${className}>
				<button type="button" class="mdc-header-accordion__title" @click=${this.toggleOpen} aria-expanded=${this.isOpen}>
					${this.title}
					<div class="mdc-header-accordion__chevron" aria-hidden="true"></div>
				</button>
				<div class="mdc-header-accordion__links-container">
					<ul class="mdc-header-accordion__links">
						${this.links.map(
              (link) => html`
								<li class="mdc-header-accordion__link-item">
									<a href=${link.href} class="mdc-header-accordion__link">${link.label}</a>
								</li>
							`
            )}
					</ul>
				</div>
			</div>
		`
  }
}
